import { useState } from "react";
import type { SyntheticEvent } from "react";
import { TriangleAlert } from "lucide-react";
import { adminApi } from "../../api";
import { Button, Modal } from "../ui";
import type { AppUser } from "../../types/user";

type DeleteUserModalProps = {
  user: AppUser;
  onClose: () => void;
  onSuccess: () => void;
};

export default function DeleteUserModal({ user, onClose, onSuccess }: DeleteUserModalProps) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleDelete(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.deleteUserPermanent(user.id, confirmEmail);
      onSuccess();
    } catch { return; } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      title="Delete user"
      onSubmit={handleDelete}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="danger"
            disabled={loading || confirmEmail.toLowerCase() !== user.email.toLowerCase()}
          >
            {loading ? "Deleting..." : "Permanently delete"}
          </Button>
        </>
      }
    >
      <div className="mb-4 flex gap-3 rounded-lg bg-red-50 p-3 ring-1 ring-red-200">
        <TriangleAlert size={18} className="mt-0.5 shrink-0 text-red-500" />
        <p className="text-sm text-red-700">
          This will <strong>permanently delete</strong> {user.full_name}'s account and all associated data. This action cannot be undone.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Type <span className="font-mono font-semibold text-gray-900">{user.email}</span> to confirm
          </label>
          <input
            type="email"
            required
            autoFocus
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={user.email}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
          />
        </div>
      </div>
    </Modal>
  );
}
